import { hasHanScript } from "@-/dev.improve.haniverse.lib.unicode";

/**
 * One jieba token of the target sentence.
 *
 * A superset of `SentenceWord` (`@-/dev.improve.haniverse.lib.sentence.words`) plus the char offset
 * `tokenize()` reports, so a caller can highlight the token in place. Assembling the two is the
 * React layer's job; this package takes the finished list.
 */
export type SentenceDrillToken = {
  /** 0-based offset of `word` within the sentence, in code points. */
  charOffset: number;
  /**
   * Tone-number pinyin for the whole token (space-separated syllables), or `null` for punctuation,
   * latin runs, and Han words the dictionary has no reading for.
   */
  pinyin: string | null;
  word: string;
};

/**
 * What the user produced, as a sequence of tokens.
 *
 * Both input modes normalize to this shape so the reducer never has to know which one it is
 * serving: speech re-segments its transcript with the same jieba path the target sentence used,
 * and the character mode emits one entry per completed word. `word` may be empty when the user
 * supplied only a reading (typed pinyin with no characters chosen yet), which the matcher handles
 * through its pinyin-equivalence rule.
 */
export type ProducedToken = {
  pinyin: string | null;
  word: string;
};

/**
 * Whether a token is something the user is expected to produce.
 *
 * Punctuation, latin runs, and whitespace are NOT: nobody says a comma, and asking the user to
 * "fill in" one would stall the drill on a blank that can never be satisfied. Non-scorable tokens
 * are pre-filled at construction and skipped by matching, cursor movement, and rating alike.
 */
export function isScorableToken(token: SentenceDrillToken): boolean {
  return hasHanScript(token.word);
}
